import { useConfirmationModal } from '@common/confirmation.hooks';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { useFetchCompanyTypes } from '@company/companyType/companyType.queries';
import { IProjectCompany } from '@company/projectCompany/projectCompany.models';
import {
  FormFieldMethods,
  Icon,
  IOption,
  removeItemInArray,
  replaceItemInArray,
  Tooltip,
  useForm,
} from '@gcl/react-core';
import { IProjectRole, IProjectRoleOption } from '@role/projectRole/projectRole.models';
import { useGetRolesProjectRequest } from '@role/projectRole/projectRole.queries';
import { useCompanyModal } from '@userProfile/list/CompanyModal/companyModal.hooks';
import { ECompanyFormKeys, ECompanyFormType } from '@userProfile/list/CompanyModal/companyModal.models';
import { useMemberModal } from '@userProfile/list/MemberModal/memberModal.hooks';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import React, { useMemo, useState } from 'react';
import { ICompanyType } from '@company/companyType/companyType.models';
import { AxiosErrorResponse } from '@core/error.models';
import { UseQueryResult } from '@tanstack/react-query';
import { ProjectRequestDetailCompanyUser } from './ProjectRequestDetailCompanyUsers';
import { EProjectRequestFormKeys, IProjectRequestFormProjectCompany } from './projectRequest.models';

interface IProjectRequestDetailCompaniesProps {
  projectId?: number;
  mainFormFieldMethods: FormFieldMethods;
  isReadOnly: boolean;
}
export const ProjectRequestDetailCompanies = ({
  projectId,
  mainFormFieldMethods,
  isReadOnly,
}: IProjectRequestDetailCompaniesProps) => {
  const companies: IProjectRequestFormProjectCompany[] =
    mainFormFieldMethods.watch(EProjectRequestFormKeys.COMPANIES) ?? [];

  const [selectedCompany, setSelectedCompany] = useState<IProjectRequestFormProjectCompany>();
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState<number>(-1);

  const [selectedMember, setSelectedMember] = useState<IProjectUserProfile | undefined>(undefined);

  const { data: projectRolesOptionsOfSelectedCompany } = useGetRolesProjectRequest(
    {
      companyTypeId: selectedCompany?.companyTypeId,
      organizationTypeId: selectedCompany?.organizationTypeId,
    },
    selectedCompany !== undefined,
  );
  const { data: companyTypeOptions } = (
    useFetchCompanyTypes({}) as () => UseQueryResult<IOption<ICompanyType>[], AxiosErrorResponse>
  )();
  const selectedCompanyRoles: { [key: string]: IProjectUserProfile[] } = mainFormFieldMethods.watch(
    `${EProjectRequestFormKeys.COMPANIES}[${selectedCompanyIndex}].${EProjectRequestFormKeys.ROLES}`,
  );

  const mapSelectedCompanyRolesWithUsers: IProjectRole[] = useMemo(() => {
    if (!selectedCompanyRoles) return [];
    return ((projectRolesOptionsOfSelectedCompany ?? []) as IProjectRoleOption[]).map((role) => {
      return {
        ...role,
        users: selectedCompanyRoles[role.roleId],
        rowVersion: '',
      };
    });
  }, [JSON.stringify(selectedCompanyRoles)]);

  const validateCompanyRoles = (selectedCompanyRolesLocal: { [key: string]: IProjectUserProfile[] }) => {
    if (selectedCompanyIndex < 0 || !selectedCompanyRolesLocal) return;
    Object.keys(selectedCompanyRolesLocal).map((roleId) => {
      if ((selectedCompanyRolesLocal[roleId]?.length ?? 0) <= 0) {
        mainFormFieldMethods.setError(
          `${EProjectRequestFormKeys.COMPANIES}[${selectedCompanyIndex}].${EProjectRequestFormKeys.ROLES}.${roleId}`,
          { message: 'At least one should be selected' },
        );
        return;
      }
      mainFormFieldMethods.clearErrors(
        `${EProjectRequestFormKeys.COMPANIES}[${selectedCompanyIndex}].${EProjectRequestFormKeys.ROLES}.${roleId}`,
      );
    });
  };

  // #region useForm
  const useMemberCreateUpdateForm = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const useCompanyCreateUpdateForm = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  // #endregion useForm

  // #region useModal
  const {
    renderModal: renderCompanyModal,
    openPortal: openCompanyModal,
    closePortal: closeCompanyModal,
  } = useCompanyModal({
    onConfirm: (updateCompany?: IProjectCompany) => {
      useCompanyCreateUpdateForm.clearErrors();
      useCompanyCreateUpdateForm.handleSubmit(onCreateUpdateCompany)();
    },
    projectId,
  });
  const {
    renderModal: renderMemberModal,
    openPortal: openMemberModal,
    closePortal: closeMemberModal,
  } = useMemberModal({
    onConfirm: (updateMember?: IProjectUserProfile) => {
      useMemberCreateUpdateForm.handleSubmit(onCreateUpdateMember(updateMember))();
    },
  });
  const { renderModal: renderDeleteCompanyConfirmation, openPortal: openDeleteCompanyConfirmation } =
    useConfirmationModal({
      content: `Confirm to Delete company ${selectedCompany?.name}?`,
      onConfirm: () => {
        const companyIndex = companies.findIndex(
          (item) => item.code === selectedCompany?.code && item.companyTypeId === selectedCompany?.companyTypeId,
        );
        if (companyIndex < 0) return;
        const newArr = removeItemInArray(companies, companyIndex);
        mainFormFieldMethods.setValue(EProjectRequestFormKeys.COMPANIES, newArr, { shouldDirty: true });
      },
    });
  // #endregion useModal

  // #region company
  const handleCompanyFormData = (formData: any) => {
    const { MaxRiskCategory, OrganizationType, ...rest } = formData;
    return {
      ...rest,
      projectId,
      [EProjectRequestFormKeys.COMPANY_MAX_RISK_CATEGORY]: formData[ECompanyFormKeys.MAX_RISK_CATEGORY].data.category,
      [EProjectRequestFormKeys.COMPANY_TYPE_ID]: formData[ECompanyFormKeys.TYPE].data.id,
      [EProjectRequestFormKeys.COMPANY_ORGANIZATION_TYPE_ID]: formData[ECompanyFormKeys.ORGANIZATION_TYPE][0].data.id,
    };
  };
  const onCreateUpdateCompany = (formData: any) => {
    const convertedData = handleCompanyFormData(formData);

    const companyIndex = companies.findIndex(
      (item) => item.code === selectedCompany?.code && item.companyTypeId === selectedCompany?.companyTypeId,
    );
    if (companyIndex < 0) {
      mainFormFieldMethods.setValue(EProjectRequestFormKeys.COMPANIES, [...companies, convertedData], {
        shouldDirty: true,
      });
      closeCompanyModal();
      return;
    }
    if (selectedCompany?.companyTypeId !== convertedData?.companyTypeId) {
      convertedData[EProjectRequestFormKeys.ROLES] = {};
    }

    mainFormFieldMethods.setValue(
      EProjectRequestFormKeys.COMPANIES,
      replaceItemInArray(companies, { ...selectedCompany, ...convertedData }, companyIndex),
      { shouldDirty: true },
    );
    setSelectedCompany(selectedCompany);
    closeCompanyModal();
  };

  const onOpenCompanyModal = (e: any) => {
    useCompanyCreateUpdateForm.reset({});
    useCompanyCreateUpdateForm.clearErrors();
    openCompanyModal(e);
  };
  // #endregion company

  // #region member
  const onCreateUpdateMember = (updateMember?: IProjectUserProfile) => {
    return (formData: any) => {
      if (!selectedCompany || selectedCompanyIndex < 0) return;
      const selectedUserId = formData.userProfile?.id ?? updateMember?.userProfileId ?? updateMember?.userProfile?.id;
      const updatedUserRoles: IProjectRole[] = formData.roles;
      const rolesWithUsersInSelectedCompany: { [key: string]: IProjectUserProfile[] } = {
        ...selectedCompanyRoles,
      };

      ((projectRolesOptionsOfSelectedCompany ?? []) as IProjectRoleOption[]).forEach(({ roleId }) => {
        const currentUsers = rolesWithUsersInSelectedCompany[roleId] ?? [];
        const selected = updatedUserRoles.findIndex((item) => item.roleId === roleId) >= 0;
        const isFoundUser = currentUsers.findIndex((user) => user.userProfile?.id === selectedUserId) >= 0;

        if (!selected) {
          if (!isFoundUser) return;

          rolesWithUsersInSelectedCompany[roleId] = currentUsers.filter(
            (user) => user.userProfile?.id !== selectedUserId,
          );

          return;
        }
        if (isFoundUser) return;

        rolesWithUsersInSelectedCompany[roleId] = [
          ...currentUsers,
          updateMember ?? { ...formData.projectUserProfile, userProfile: formData.userProfile },
        ];
      });

      mainFormFieldMethods.setValue(
        `${EProjectRequestFormKeys.COMPANIES}[${selectedCompanyIndex}].${EProjectRequestFormKeys.ROLES}`,
        rolesWithUsersInSelectedCompany,
        { shouldDirty: true },
      );
      validateCompanyRoles(rolesWithUsersInSelectedCompany);
      closeMemberModal();
    };
  };

  const onRemoveUserFromCompany = (companyId: number | undefined, projectUserProfileId: number | undefined) => {
    if (!selectedCompany || selectedCompanyIndex < 0) return;
    const rolesWithUsersInSelectedCompany: { [key: string]: IProjectUserProfile[] } =
      mainFormFieldMethods.getValues(
        `${EProjectRequestFormKeys.COMPANIES}[${selectedCompanyIndex}].${EProjectRequestFormKeys.ROLES}`,
      ) ?? {};

    ((projectRolesOptionsOfSelectedCompany ?? []) as IProjectRoleOption[]).map(({ roleId }) => {
      const currentUsers = rolesWithUsersInSelectedCompany[roleId] ?? [];
      rolesWithUsersInSelectedCompany[roleId] = currentUsers.filter(
        (user) => (user.userProfileId ?? user.userProfile?.id) !== projectUserProfileId,
      );
    });

    mainFormFieldMethods.setValue(
      `${EProjectRequestFormKeys.COMPANIES}[${selectedCompanyIndex}].${EProjectRequestFormKeys.ROLES}`,
      rolesWithUsersInSelectedCompany,
      { shouldDirty: true },
    );
    validateCompanyRoles(rolesWithUsersInSelectedCompany);
    closeMemberModal();
  };

  const onClickOpenMemberModal = (
    e: any,
    company: IProjectCompany,
    companyIndex: number,
    user?: IProjectUserProfile,
  ) => {
    setSelectedCompany(company);
    setSelectedCompanyIndex(companyIndex);
    setSelectedMember(user);
    useMemberCreateUpdateForm.reset();
    useMemberCreateUpdateForm.clearErrors();
    openMemberModal(e);
  };
  // #endregion member

  return (
    <>
      <div className="gcl-gems-project-request-detail-company-add gcl-mb-md">
        <LabelWithMessage labelFor="gems_user_list_company_select" text="Company" />
        {isReadOnly ? undefined : (
          <Tooltip overlay="Add Company" id="gcl-gems-project-request-add-company-btn-tooltip">
            <Icon
              iconName="PlusLg"
              className="gcl-gems-project-request-detail-company-icon"
              onClick={(e) => {
                setSelectedCompany(undefined);
                onOpenCompanyModal(e);
              }}
            />
          </Tooltip>
        )}
      </div>
      {companies.map((company, index) => {
        return (
          <ProjectRequestDetailCompanyUser
            index={index}
            mainFormFieldMethods={mainFormFieldMethods}
            key={`${index.toString()}${company.code}`}
            companyType={
              companyTypeOptions?.find((item) => item.data?.id && item.data?.id === company.companyTypeId)?.data
            }
            company={company}
            onClickOpenMemberModal={onClickOpenMemberModal}
            onDeleteCompany={(toDeleteCompany) => {
              setSelectedCompany(toDeleteCompany);
              openDeleteCompanyConfirmation();
            }}
            onEditCompany={(e, toEditCompany) => {
              setSelectedCompany(toEditCompany);
              onOpenCompanyModal(e);
            }}
            isReadOnly={isReadOnly}
          />
        );
      })}

      {renderMemberModal({
        company: selectedCompany,
        projectUserProfile: selectedMember,
        rolesWithUser: mapSelectedCompanyRolesWithUsers,
        useFormMethods: useMemberCreateUpdateForm,
        removeUserFromCompany: onRemoveUserFromCompany,
        isShowLockProjectUser: false,
        type: 'projectRequest',
        isShowTeams: false,
        isShowSyncProfile: false,
      })}
      {renderDeleteCompanyConfirmation(false, false, false)}
      {renderCompanyModal({
        company: selectedCompany,
        isProjectRequestDefaultCompany: selectedCompany?.isDefault,
        useFormMethods: useCompanyCreateUpdateForm,
        type: ECompanyFormType.PROJECT_REQUEST,
      })}
    </>
  );
};
